<template>
  <div>
    <!-- ══ 1. HERO — SPLIT SCREEN ═══════════════════════════════════════ -->
    <section class="hero-split">
      <!-- Left: identity panel -->
      <div class="hero-left">
        <div class="hero-left-inner">
          <div class="hero-top-row">
            <v-img src="../assets/gckLogo1.png" contain class="hero-logo" />
            <span class="hero-badge">Established 1960</span>
          </div>
          <h1 class="hero-heading">राजकीय महाविद्यालय करौली</h1>
          <h2 class="hero-subheading">Government College Karauli</h2>
          <p class="hero-desc">Affiliated to University of Kota · NAAC Accredited · Nodal College, Rajasthan</p>
          <div class="hero-divider"></div>
          <div class="hero-btns">
            <v-btn color="secondary" size="large" rounded="lg" to="/admissions" elevation="3">
              <v-icon class="mr-2">mdi-school</v-icon>Admissions
            </v-btn>
            <v-btn variant="outlined" color="white" size="large" rounded="lg" to="/about">
              Learn More
            </v-btn>
          </div>
          <div class="hero-chips">
            <v-chip variant="outlined" color="white" size="small" prepend-icon="mdi-star-circle-outline">NAAC Accredited</v-chip>
            <v-chip variant="outlined" color="white" size="small" prepend-icon="mdi-bank-outline">UGC Recognised</v-chip>
            <v-chip variant="outlined" color="white" size="small" prepend-icon="mdi-home-city-outline">Nodal College</v-chip>
          </div>
        </div>
      </div>

      <!-- Right: carousel -->
      <div class="hero-right">
        <v-carousel
          v-if="bannersReady"
          hide-delimiters cycle :show-arrows="false"
          height="100%"
          interval="4000"
          class="hero-carousel-split"
          transition="fade-transition"
          reverse-transition="fade-transition"
        >
          <v-carousel-item v-for="(item, i) in banners" :key="i">
            <v-img :src="item.url" cover height="100%" />
          </v-carousel-item>
        </v-carousel>
        <div v-if="!bannersReady" class="hero-skeleton d-flex align-center justify-center" style="height:100%;">
          <v-progress-circular indeterminate color="white" size="48" />
        </div>
        <!-- gradient bleed from left panel into image -->
        <div class="hero-image-overlay" />
      </div>
    </section>

    <!-- ══ 2. STATS STRIP ════════════════════════════════════════════════ -->
    <section class="stats-strip">
      <v-container>
        <v-row justify="center">
          <v-col v-for="stat in stats" :key="stat.label"
            cols="6" sm="3"
            class="text-center stat-col"
          >
            <v-icon :icon="stat.icon" size="36" color="accent" class="mb-1" />
            <div class="stat-number">{{ stat.current.toLocaleString() }}{{ stat.suffix }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ══ 3. WHY CHOOSE US ════════════════════════════════════════════ -->
    <section class="why-section py-12">
      <v-container>
        <div class="text-center mb-8">
          <div class="section-tag mx-auto">Why GCK</div>
          <h2 class="section-heading mt-1">Why Choose Government College Karauli</h2>
        </div>
        <v-row class="gy-4">
          <v-col v-for="w in whyUs" :key="w.title" cols="12" sm="6" md="3">
            <v-card elevation="2" rounded="xl" class="why-card pa-5 text-center" height="100%">
              <div class="why-icon-wrap mx-auto mb-3">
                <v-icon :color="w.color" size="32">{{ w.icon }}</v-icon>
              </div>
              <div class="why-title mb-2">{{ w.title }}</div>
              <div class="why-desc">{{ w.desc }}</div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ══ 5. ABOUT + NOTIFICATIONS ══════════════════════════════════════ -->
    <section class="about-section py-12">
      <v-container>
        <v-row align="start" class="gy-6">
          <!-- About text -->
          <v-col cols="12" md="7">
            <div class="section-tag">Who We Are</div>
            <h2 class="section-heading mt-1 mb-4">About Government College Karauli</h2>
            <p class="body-text">
              Government College Karauli is one of the most prestigious educational institutions
              in Rajasthan, founded in 1960. Affiliated with the University of Kota, the college
              imparts education across Science, Humanities, and Commerce at undergraduate and
              postgraduate levels, and also offers research facilities.
            </p>
            <p class="body-text mt-3">
              Situated in the historic city of Karauli — known for the temples of Madan Mohan Ji
              and Kailadevi — the college serves as the primary education centre of the DANG
              region, welcoming students from rural and interior areas. It is accredited by
              NAAC-UGC and declared a Nodal College and Resource Centre by the Government of
              Rajasthan.
            </p>
            <v-btn
              variant="outlined" color="primary" rounded="lg" class="mt-5"
              to="/about"
            >
              Read Full History <v-icon end>mdi-arrow-right</v-icon>
            </v-btn>
          </v-col>

          <!-- Notifications -->
          <v-col cols="12" md="5">
            <v-card elevation="3" rounded="xl" class="notif-card">
              <v-card-title class="notif-header pa-4">
                <v-icon class="mr-2" color="secondary">mdi-bell-ring-outline</v-icon>
                Notifications & Circulars
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-0" style="height:260px; overflow:hidden;">
                <Vue3Marquee
                  :vertical="true" :pauseOnHover="true"
                  :duration="20" :loop="0" :clone="false"
                  class="notif-marquee"
                >
                  <div
                    v-for="(n, i) in notifications" :key="i"
                    class="notif-item"
                    @click="openNotif(n)"
                  >
                    <v-icon size="14" color="secondary" class="mr-2 flex-shrink-0">mdi-circle-small</v-icon>
                    <span class="notif-text">{{ n.title }}</span>
                    <v-icon v-if="n.link" size="14" class="ml-1 flex-shrink-0" color="primary">mdi-open-in-new</v-icon>
                  </div>
                </Vue3Marquee>
              </v-card-text>
              <v-divider />
              <v-card-actions class="pa-3 justify-center">
                <v-btn variant="text" color="primary" size="small" to="/circular">
                  View All Circulars <v-icon end size="14">mdi-arrow-right</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ══ 4. PRINCIPAL'S MESSAGE ════════════════════════════════════════ -->
    <section class="principal-section py-12">
      <v-container>
        <v-row align="center" justify="center">
          <v-col cols="12" md="3" class="text-center text-md-right">
            <v-avatar size="200" class="principal-avatar elevation-6">
              <v-img src="../assets/principal.jpg" cover />
            </v-avatar>
          </v-col>
          <v-col cols="12" md="7">
            <div class="section-tag">Message from the Principal</div>
            <h2 class="section-heading mt-1">Sh. Rafique Ahemad</h2>
            <div class="principal-designation mb-4">Principal, Government College Karauli</div>
            <blockquote class="principal-quote">
              "Since its inception in 1960, Government College Karauli has stood as a beacon of
              knowledge, nurturing young minds and fostering a culture of learning. Our institution
              sculpts individuals ready to face the challenges of tomorrow — not just providing
              degrees but igniting a passion for lifelong learning."
            </blockquote>
            <v-btn variant="outlined" color="primary" rounded="lg" class="mt-4" to="/principal_desk">
              Read Full Message <v-icon end>mdi-arrow-right</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ══ 5. QUICK ACCESS ═══════════════════════════════════════════════ -->
    <section class="quicklinks-section py-12">
      <v-container>
        <div class="text-center mb-8">
          <div class="section-tag mx-auto">Quick Access</div>
          <h2 class="section-heading mt-1">Important Links</h2>
        </div>
        <v-row justify="center" class="gy-4">
          <v-col
            v-for="link in quickLinks" :key="link.label"
            cols="6" sm="4" md="2"
          >
            <v-card
              class="quicklink-card text-center pa-4"
              elevation="2" rounded="xl"
              @click="navigateQuick(link)"
              style="cursor:pointer;"
            >
              <v-icon :color="link.color" size="40" class="mb-2">{{ link.icon }}</v-icon>
              <div class="quicklink-label">{{ link.label }}</div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ══ 7. DEPARTMENTS ════════════════════════════════════════════════ -->
    <section class="dept-section py-12">
      <v-container>
        <div class="text-center mb-8">
          <div class="section-tag mx-auto">Academics</div>
          <h2 class="section-heading mt-1">Our Departments</h2>
        </div>
        <v-row class="gy-4">
          <v-col v-for="dept in departments" :key="dept.name" cols="6" sm="4" md="3">
            <v-card
              elevation="1" rounded="xl" class="dept-card pa-4"
              @click="$router.push(dept.path)" style="cursor:pointer;"
            >
              <v-icon :color="dept.color" size="28" class="mb-2">{{ dept.icon }}</v-icon>
              <div class="dept-name">{{ dept.name }}</div>
            </v-card>
          </v-col>
        </v-row>
        <div class="text-center mt-6">
          <v-btn variant="outlined" color="primary" rounded="lg" to="/departments">
            View All Departments <v-icon end>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- ══ 8. EVENT GALLERY STRIP ════════════════════════════════════════ -->
    <section class="gallery-section py-12">
      <v-container>
        <div class="text-center mb-6">
          <div class="section-tag mx-auto">Campus Life</div>
          <h2 class="section-heading mt-1">Event Gallery</h2>
        </div>
        <v-row class="gy-3 gx-3">
          <v-col
            v-for="(img, i) in galleryImages" :key="i"
            cols="6" sm="4" md="2"
          >
            <div class="gallery-thumb" @click="galleryDialog = true; galleryIndex = i">
              <v-img :src="img.thumb_url" cover height="130" class="rounded-lg" />
              <div class="gallery-thumb-overlay">
                <v-icon color="white" size="22">mdi-magnify-plus-outline</v-icon>
              </div>
            </div>
          </v-col>
        </v-row>
        <div class="text-center mt-6">
          <v-btn variant="outlined" color="primary" rounded="lg" to="/events">
            View All Events <v-icon end>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- Gallery lightbox -->
    <v-dialog v-model="galleryDialog" max-width="900">
      <v-carousel v-model="galleryIndex" hide-delimiters height="500">
        <v-carousel-item
          v-for="(img, i) in galleryImages" :key="i"
          :src="img.original_url" cover
        />
      </v-carousel>
    </v-dialog>

    <!-- ══ 9. CAMPUS HIGHLIGHTS ════════════════════════════════════════ -->
    <section class="campus-section py-12">
      <v-container>
        <div class="text-center mb-8">
          <div class="section-tag mx-auto">Infrastructure</div>
          <h2 class="section-heading mt-1">Campus Facilities</h2>
        </div>
        <v-row class="gy-4">
          <v-col v-for="fac in facilities" :key="fac.label" cols="12" sm="6" md="4">
            <v-card
              elevation="2" rounded="xl" class="facility-card overflow-hidden"
              @click="$router.push(fac.path)" style="cursor:pointer;"
            >
              <v-img :src="fac.img" cover height="160" class="facility-img">
                <div class="facility-overlay">
                  <v-icon color="white" size="28" class="mb-1">{{ fac.icon }}</v-icon>
                  <div class="facility-label">{{ fac.label }}</div>
                </div>
              </v-img>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ══ 10. ACCREDITATION BAR ══════════════════════════════════════════ -->
    <section class="accred-section py-8">
      <v-container>
        <v-row justify="center" align="center" class="gy-3">
          <v-col cols="12" class="text-center">
            <div class="section-tag mx-auto mb-2">Recognition</div>
          </v-col>
          <v-col
            v-for="badge in accredBadges" :key="badge.label"
            cols="12" sm="6" md="3"
            class="d-flex justify-center"
          >
            <v-chip
              variant="outlined" color="primary" size="large"
              class="accred-chip"
              :prepend-icon="badge.icon"
            >
              {{ badge.label }}
            </v-chip>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ══ 8. VOTER HELPLINE ════════════════════════════════════════════ -->
    <section class="py-4 text-center">
      <a
        href="https://www.eci.gov.in/voter-helpline-app/"
        target="_blank"
        class="voter-link"
      >
        <v-icon class="mr-1" size="16">mdi-vote</v-icon>
        Voter Helpline Registration
      </a>
    </section>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { APIService } from '../Utils/APIService'
const apiService = new APIService()

export default defineComponent({
  name: 'HomeView',
  components: {},
  data () {
    return {
      banners: [],
      bannersReady: false,
      notifications: [],
      bannerLoading: true,
      errors: [],
      galleryImages: [],
      galleryDialog: false,
      galleryIndex: 0,
      stats: [
        { icon: 'mdi-account-group',       target: 3400, suffix: '+', label: 'Students',           current: 0 },
        { icon: 'mdi-account-tie-outline', target: 40,   suffix: '+', label: 'Faculty',             current: 0 },
        { icon: 'mdi-calendar-star',       target: 64,   suffix: '',  label: 'Years of Excellence', current: 0 },
        { icon: 'mdi-domain',              target: 17,   suffix: '',  label: 'Departments',          current: 0 },
      ],
      quickLinks: [
        { label: 'Admissions',   icon: 'mdi-school-outline',        color: 'primary',   path: '/admissions',     ext: false },
        { label: 'Circulars',    icon: 'mdi-file-document-outline', color: 'secondary', path: '/circular',       ext: false },
        { label: 'Scholarships', icon: 'mdi-medal-outline',         color: 'success',   path: '/scholarships',   ext: false },
        { label: 'IQAC',         icon: 'mdi-clipboard-check-outline', color: 'info',    path: '/iqac_all',       ext: false },
        { label: 'Events',       icon: 'mdi-image-multiple-outline', color: 'warning',  path: '/events',         ext: false },
        { label: 'Contact',      icon: 'mdi-phone-outline',          color: 'error',    path: '/contact',        ext: false },
      ],
      whyUs: [
        { icon: 'mdi-flask-outline',          color: 'primary',   title: 'Modern Laboratories',   desc: 'Fully equipped labs for Botany, Chemistry, Physics, Geography, Zoology, and ICT.' },
        { icon: 'mdi-book-open-page-variant', color: 'secondary', title: 'Rich Library',           desc: 'Over 72,615 volumes, INFLIBNET membership, and digital resources.' },
        { icon: 'mdi-trophy-outline',         color: 'warning',   title: 'Sports & Yoga',          desc: 'Extensive sports grounds and regular Yoga sessions for holistic wellness.' },
        { icon: 'mdi-certificate-outline',    color: 'success',   title: 'NAAC Accredited',        desc: 'Recognised as a Centre of Excellence and Nodal College by the Government of Rajasthan.' },
        { icon: 'mdi-account-group-outline',  color: 'info',      title: 'Student Support',        desc: 'NSS, NCC, placement cell, scholarships, and women\'s safety initiatives.' },
        { icon: 'mdi-microscope',             color: 'error',     title: 'Research Centre',        desc: 'Active research centre under University of Kota for postgraduate scholars.' },
        { icon: 'mdi-bus-school',             color: 'teal',      title: 'Accessible Location',    desc: 'Located in Karauli city, serving students from the DANG region of Rajasthan.' },
        { icon: 'mdi-currency-inr',           color: 'deep-orange', title: 'Affordable Education', desc: 'Government-subsidised fee structure making quality education accessible to all.' },
      ],
      departments: [],
      facilities: [
        { icon: 'mdi-bookshelf',         label: 'Library',         path: '/library',         img: 'https://api.govtcollegekarauli.ac.in/storage/850/4.2.1-Library---Geotage-Photos-Aditional-Information_page-0002.jpg' },
        { icon: 'mdi-monitor-dashboard', label: 'ICT Lab',         path: '/ict_lab',         img: 'https://api.govtcollegekarauli.ac.in/storage/848/E-Class-1.jpeg' },
        { icon: 'mdi-presentation',      label: 'Conference Hall', path: '/conference_hall', img: 'https://api.govtcollegekarauli.ac.in/storage/845/Conference-Hall-1.jpeg' },
        { icon: 'mdi-soccer-field',      label: 'Sports Ground',   path: '/sports_and_yoga', img: 'https://api.govtcollegekarauli.ac.in/storage/854/WhatsApp-Image-2025-01-25-at-1.50.38-PM-(1).jpeg' },
        { icon: 'mdi-flask-outline',     label: 'Science Labs',    path: '/botany_lab',      img: 'https://api.govtcollegekarauli.ac.in/storage/851/4.2.1-Library---Geotage-Photos-Aditional-Information_page-0003.jpg' },
        { icon: 'mdi-home-account',      label: 'Girls Hostel',    path: '/girls_hostel',    img: 'https://api.govtcollegekarauli.ac.in/storage/852/4.2.1-Library---Geotage-Photos-Aditional-Information_page-0004.jpg' },
      ],
      accredBadges: [
        { icon: 'mdi-star-circle-outline',   label: 'NAAC Accredited' },
        { icon: 'mdi-bank-outline',          label: 'UGC Recognised' },
        { icon: 'mdi-home-city-outline',     label: 'Nodal College – Rajasthan' },
        { icon: 'mdi-equal-box',             label: 'Centre for Equal Opportunity' },
      ],
    }
  },
  methods: {
    getBanners () {
      this.bannerLoading = true
      apiService.getBanners()
        .then(res => {
          if (res.status === 200) {
            this.banners = res.data.data
            this.preloadBanners()
          }
        })
        .catch(e => { this.errors.push(e); this.bannersReady = true })
    },
    preloadBanners () {
      let loaded = 0
      const total = this.banners.length
      if (total === 0) { this.bannersReady = true; return }
      this.banners.forEach(b => {
        const img = new Image()
        img.onload = img.onerror = () => {
          loaded++
          if (loaded === total) this.bannersReady = true
        }
        img.src = b.url
      })
    },
    getCirculars () {
      apiService.getCirculars()
        .then(res => {
          if (res.status === 200) {
            this.notifications = res.data.data.data.slice(0, 20)
          }
        })
        .catch(e => this.errors.push(e))
    },
    openNotif (n) {
      if (n.link) window.open(n.link, '_blank')
    },
    getDepartments () {
      const iconMap = {
        'mathematics':           { icon: 'mdi-math-compass',              color: 'indigo'       },
        'physics':               { icon: 'mdi-atom',                      color: 'blue'         },
        'chemistry':             { icon: 'mdi-flask-outline',             color: 'green'        },
        'botany':                { icon: 'mdi-leaf',                      color: 'light-green'  },
        'zoology':               { icon: 'mdi-paw-outline',               color: 'teal'         },
        'geography':             { icon: 'mdi-earth',                     color: 'cyan'         },
        'hindi':                 { icon: 'mdi-translate',                 color: 'orange'       },
        'english':               { icon: 'mdi-book-alphabet',             color: 'blue-grey'    },
        'sanskrit':              { icon: 'mdi-script-text-outline',       color: 'amber'        },
        'history':               { icon: 'mdi-bank-outline',              color: 'brown'        },
        'economics':             { icon: 'mdi-chart-line',                color: 'deep-orange'  },
        'political science':     { icon: 'mdi-vote-outline',              color: 'red'          },
        'sociology':             { icon: 'mdi-account-group-outline',     color: 'purple'       },
        'psychology':            { icon: 'mdi-brain',                     color: 'pink'         },
        'accountancy':           { icon: 'mdi-calculator',                color: 'deep-orange'  },
        'business administration': { icon: 'mdi-briefcase-outline',       color: 'deep-orange'  },
      }
      apiService.getDepartments()
        .then(res => {
          if (res.status === 200) {
            const subDepts = []
            for (const group of (res.data.data || [])) {
              for (const dept of (group.sub_dept || [])) {
                const key = (dept.name || '').toLowerCase()
                const meta = iconMap[key] || { icon: 'mdi-domain', color: 'primary' }
                subDepts.push({
                  name: dept.name,
                  icon: meta.icon,
                  color: meta.color,
                  path: `/faculties/${dept.id}`,
                })
              }
            }
            this.departments = subDepts.slice(0, 8)
          }
        })
        .catch(() => {})
    },
    navigateQuick (link) {
      if (link.ext) window.open(link.path, '_blank')
      else this.$router.push(link.path)
    },
    animateCounters () {
      const duration = 6000
      const steps = 60
      const interval = duration / steps
      this.stats.forEach(stat => {
        let step = 0
        const increment = stat.target / steps
        const timer = setInterval(() => {
          step++
          stat.current = Math.min(Math.round(increment * step), stat.target)
          if (step >= steps) clearInterval(timer)
        }, interval)
      })
    },
  },
  beforeMount () { this.getBanners() },
  mounted () {
    this.getCirculars()
    this.getDepartments()
    this.animateCounters()
    apiService.getImages().then(res => {
      if (res.status === 200) this.galleryImages = res.data.data.slice(0, 12)
    }).catch(() => {})
  },
})
</script>

<style scoped>
/* ── Hero Split ──────────────────────────────────────────── */
.hero-split {
  display: flex;
  height: 580px;
}

/* Left panel */
.hero-left {
  width: 46%;
  flex-shrink: 0;
  background: linear-gradient(155deg, #070d1f 0%, #0f1d5e 45%, #1a237e 75%, #1e2fa3 100%);
  display: flex;
  align-items: center;
  padding: 44px 52px;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

/* decorative circles */
.hero-left::before {
  content: '';
  position: absolute;
  top: -80px;
  right: -80px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(245,124,0,0.18) 0%, transparent 70%);
  pointer-events: none;
}
.hero-left::after {
  content: '';
  position: absolute;
  right: -50px;
  top: 0;
  bottom: 0;
  width: 100px;
  background: linear-gradient(155deg, #070d1f 0%, #0f1d5e 45%, #1a237e 75%, #1e2fa3 100%);
  clip-path: polygon(0 0, 0% 100%, 100% 100%);
  z-index: 2;
}
/* bottom-left accent circle */
.hero-left-inner {
  position: relative;
  z-index: 3;
  width: 100%;
}
.hero-left-inner::before {
  content: '';
  position: absolute;
  bottom: -100px;
  left: -60px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(100,120,255,0.15) 0%, transparent 70%);
  pointer-events: none;
}

/* logo + badge row */
.hero-top-row { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
.hero-logo    { flex: 0 0 56px !important; max-width: 56px !important; height: 56px; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.4)); }
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(90deg, #E65100, #F57C00);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  padding: 4px 14px;
  border-radius: 20px;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(245,124,0,0.4);
}

/* headings */
.hero-heading {
  color: #fff;
  font-size: clamp(1.35rem, 2.6vw, 2.05rem);
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 6px 0;
  text-shadow: 0 2px 12px rgba(0,0,0,0.3);
}
.hero-subheading {
  color: #FFD740;
  font-size: clamp(1rem, 1.7vw, 1.25rem);
  font-weight: 700;
  margin: 0 0 8px 0;
  letter-spacing: 0.3px;
}
.hero-desc {
  color: rgba(255,255,255,0.72);
  font-size: 0.83rem;
  line-height: 1.7;
  margin: 0;
}

/* divider */
.hero-divider {
  height: 3px;
  width: 48px;
  background: linear-gradient(90deg, #F57C00, #FFD740);
  border-radius: 2px;
  margin: 16px 0;
  box-shadow: 0 0 8px rgba(245,124,0,0.5);
}

/* buttons row */
.hero-btns { display: flex; flex-wrap: wrap; gap: 12px; }

/* chips row */
.hero-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
.hero-chips .v-chip { backdrop-filter: blur(4px); background: rgba(255,255,255,0.07) !important; }

/* Right panel */
.hero-right {
  flex: 1;
  position: relative;
  overflow: hidden;
}
.hero-carousel-split { height: 100% !important; }
.hero-skeleton { background: #0f1d5e; }

/* gradient overlay bleeding from left panel into image */
.hero-image-overlay {
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: 220px;
  background: linear-gradient(to right, #1a237e 0%, rgba(26,35,126,0.5) 50%, transparent 100%);
  z-index: 1;
  pointer-events: none;
}

/* Mobile: stack hero vertically */
@media (max-width: 767px) {
  .hero-split { flex-direction: column; height: auto; }
  .hero-left  { width: 100%; padding: 36px 24px; }
  .hero-left::after { display: none; }
  .hero-right { height: 260px; }
  .hero-image-overlay { width: 80px; }
}

/* ── Stats strip ─────────────────────────────────────────── */
.stats-strip {
  background: linear-gradient(135deg, #0f1d5e 0%, #1a237e 50%, #283593 100%);
  padding: 28px 0;
  box-shadow: 0 4px 20px rgba(10,20,80,0.3);
}
.stat-col { border-right: 1px solid rgba(255,255,255,0.10); }
.stat-col:last-child { border-right: none; }
.stat-number {
  font-size: clamp(1.7rem, 4vw, 2.3rem);
  font-weight: 800;
  color: #fff;
  line-height: 1;
}
.stat-label { font-size: 0.75rem; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: 1px; margin-top: 5px; }

/* ── About ───────────────────────────────────────────────── */
.about-section { background: #F4F7FF; }

.section-tag {
  display: inline-block;
  background: rgba(245,124,0,0.12);
  color: #F57C00;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 3px 12px;
  border-radius: 20px;
}
.section-heading {
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 700;
  color: #1A237E;
  line-height: 1.25;
}
.body-text { font-size: 0.95rem; color: #455A64; line-height: 1.8; }

/* ── Notification card ───────────────────────────────────── */
.notif-card { border-top: 3px solid #1A237E; }
.notif-header { font-size: 0.95rem !important; font-weight: 700; color: #1A237E; }
.notif-item {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;
}
.notif-marquee :deep(.marquee-content) { align-items: flex-start !important; }
.notif-marquee :deep(.marquee) { align-items: flex-start !important; }
.notif-item:hover { background: #F4F7FF; }
.notif-text { font-size: 0.82rem; color: #37474F; line-height: 1.5; }

/* ── Principal ───────────────────────────────────────────── */
.principal-section { background: #fff; }
.principal-avatar { border: 4px solid #1A237E; }
.principal-designation { font-size: 0.88rem; color: #F57C00; font-weight: 600; }
.principal-quote {
  font-size: 1rem;
  color: #455A64;
  line-height: 1.8;
  border-left: 4px solid #1A237E;
  padding-left: 16px;
  margin: 0;
  font-style: italic;
}

/* ── Quick links ─────────────────────────────────────────── */
.quicklinks-section { background: #F4F7FF; }
.quicklink-card { transition: transform 0.2s, box-shadow 0.2s; border: 1px solid #e8eaf6; }
.quicklink-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(26,35,126,0.15) !important; }
.quicklink-label { font-size: 0.8rem; font-weight: 600; color: #1A237E; margin-top: 4px; }

/* ── Gallery ─────────────────────────────────────────────── */
.gallery-section { background: #fff; }
.gallery-thumb {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
}
.gallery-thumb-overlay {
  position: absolute;
  inset: 0;
  background: rgba(26,35,126,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 8px;
}
.gallery-thumb:hover .gallery-thumb-overlay { opacity: 1; }

/* ── Why Choose Us ───────────────────────────────────────── */
.why-section { background: #fff; }
.why-icon-wrap {
  width: 64px; height: 64px; border-radius: 16px;
  background: #F4F7FF;
  display: flex; align-items: center; justify-content: center;
}
.why-card { border: 1px solid #e8eaf6; transition: transform 0.2s, box-shadow 0.2s; }
.why-card:hover { transform: translateY(-4px); box-shadow: 0 10px 28px rgba(26,35,126,0.12) !important; }
.why-title { font-size: 0.92rem; font-weight: 700; color: #1A237E; }
.why-desc  { font-size: 0.8rem; color: #607D8B; line-height: 1.6; }

/* ── Departments ─────────────────────────────────────────── */
.dept-section { background: #F4F7FF; }
.dept-card { border: 1px solid #e8eaf6; transition: transform 0.2s, box-shadow 0.2s; }
.dept-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(26,35,126,0.12) !important; }
.dept-stream { font-size: 0.68rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #F57C00; margin-bottom: 2px; }
.dept-name   { font-size: 0.88rem; font-weight: 700; color: #1A237E; line-height: 1.3; }

/* ── Campus Facilities ───────────────────────────────────── */
.campus-section { background: #fff; }
.facility-card { transition: transform 0.2s, box-shadow 0.2s; }
.facility-card:hover { transform: translateY(-4px); box-shadow: 0 10px 28px rgba(26,35,126,0.18) !important; }
.facility-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(26,35,126,0.85) 0%, rgba(26,35,126,0.2) 100%);
  display: flex; flex-direction: column;
  align-items: center; justify-content: flex-end;
  padding-bottom: 16px;
}
.facility-label { font-size: 0.92rem; font-weight: 700; color: #fff; text-shadow: 0 1px 4px rgba(0,0,0,0.4); }

/* ── Accreditation ───────────────────────────────────────── */
.accred-section { background: #F4F7FF; }
.accred-chip { font-size: 0.8rem !important; font-weight: 600 !important; }

/* ── Voter link ──────────────────────────────────────────── */
.voter-link {
  font-size: 0.85rem;
  color: #1A237E;
  text-decoration: none;
  font-weight: 500;
}
.voter-link:hover { text-decoration: underline; }
</style>
